import React, { createContext, useContext, useEffect, useState, useCallback } from "react";
import api, { formatApiError } from "../lib/api";
import { track } from "../lib/analytics";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null); // null=checking, false=guest, object=user
  const [loading, setLoading] = useState(true);

  const refresh = useCallback(async () => {
    const token = localStorage.getItem("pm_token");
    if (!token) { setUser(false); setLoading(false); return; }
    try {
      const { data } = await api.get("/auth/me");
      setUser(data.user);
    } catch {
      localStorage.removeItem("pm_token");
      setUser(false);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  const login = async (mobile, password, remember) => {
    const { data } = await api.post("/auth/login", { mobile, password, remember });
    localStorage.setItem("pm_token", data.access_token);
    setUser(data.user);
    track("login");
    return data.user;
  };

  const register = async (name, mobile, password, extra = {}) => {
    const { data } = await api.post("/auth/register", { name, mobile, password, ...extra });
    localStorage.setItem("pm_token", data.access_token);
    setUser(data.user);
    track("register_completed");
    return data;
  };

  const setupRecovery = async (secret_number, secret_image) => {
    await api.post("/auth/setup-recovery", { secret_number, secret_image });
    track("recovery_completed");
    await refresh();
  };

  const logout = async () => {
    try { await api.post("/auth/logout"); } catch {}
    localStorage.removeItem("pm_token");
    setUser(false);
  };

  const updateUser = (u) => setUser((prev) => ({ ...prev, ...u }));

  return (
    <AuthContext.Provider value={{ user, loading, login, register, setupRecovery, logout, refresh, updateUser, formatApiError }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within AuthProvider");
  return ctx;
}
